/**
 * EmailRequirementsList — inline-чек-лист требований к email + 5-сегментная
 * прогресс-полоска (по аналогии с PasswordStrengthMeter).
 *
 * По UX-спеку (акционер): подсказка появляется только когда `value` не пустое
 * и хотя бы один пункт не пройден. Если всё ок — компонент рендерит null,
 * чтобы не раздражать пользователя.
 */
import { emailChecks } from '@/lib/validation'
import { Icon } from '@/components/icons/Icon'

interface Props {
  value: string
  /** Если true — показывает компонент даже когда всё пройдено (для showcase). */
  alwaysShow?: boolean
  className?: string
}

const TOTAL = 5

export function EmailRequirementsList({ value, alwaysShow = false, className }: Props) {
  const checks = emailChecks(value)
  const passed = checks.filter((c) => c.passed).length
  const allPassed = passed === TOTAL
  const isEmpty = value.trim().length === 0

  if (!alwaysShow && (isEmpty || allPassed)) return null

  // Color интерполяции для прогресс-бара
  const segmentColor =
    passed <= 1 ? 'bg-status-danger-text'
    : passed <= 3 ? 'bg-status-warning-text'
    : passed === 4 ? 'bg-accent-cool'
    : 'bg-status-success-text'

  return (
    <div className={`space-y-2 ${className ?? ''}`} aria-live="polite">
      <div className="flex gap-1" role="progressbar" aria-valuemin={0} aria-valuemax={TOTAL} aria-valuenow={passed}>
        {Array.from({ length: TOTAL }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ease-out ${
              i < passed ? segmentColor : 'bg-border-strong'
            }`}
          />
        ))}
      </div>
      <ul className="space-y-1">
        {checks.map((c) => (
          <li
            key={c.label}
            className={`flex items-center gap-2 text-xs transition-colors ${
              c.passed ? 'text-status-success-text' : 'text-text-secondary'
            }`}
          >
            <Icon
              name={c.passed ? 'check-circle' : 'circle'}
              size={14}
              aria-hidden="true"
              className="flex-shrink-0"
            />
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
